import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Card, Typography, Tabs, Tab, Slider, Tooltip, ToggleButton, ToggleButtonGroup, Skeleton
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CheckCircleRounded as CorrectIcon, CancelRounded as MissedIcon, HistoryRounded as EmptyIcon,
  QueryStatsRounded as NoDataIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '../hooks';
import { tokens, outcomeLabel } from '../theme';
import { PageHeader, StatTile, EmptyState, ErrorState, OutcomePill, pct, segmentedSx } from './ui';

// Midpoints of the worker's confidence buckets, i.e. what a calibrated model should hit
const EXPECTED = { '30-40%': 0.35, '40-50%': 0.45, '50-60%': 0.55, '60-70%': 0.65, '70-80%': 0.75, '80%+': 0.85 };

function CalibrationChart({ ranges }) {
  const H = 180;
  return (
    <Box>
      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <Box display="flex" alignItems="center" gap={0.75}>
          <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: tokens.accent }} />
          <Typography variant="caption" color="text.secondary">Actual win rate</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.75}>
          <Box sx={{ width: 14, height: 2, bgcolor: tokens.text }} />
          <Typography variant="caption" color="text.secondary">Expected if calibrated</Typography>
        </Box>
      </Box>
      <Box display="grid" gridTemplateColumns="28px minmax(0,1fr)" gap={1}>
        {/* y axis */}
        <Box position="relative" height={H}>
          {[0, 50, 100].map(v => (
            <Typography key={v} variant="caption" color="text.secondary" sx={{ position: 'absolute', right: 0, bottom: `${v}%`, transform: 'translateY(50%)', fontSize: 10 }}>
              {v}%
            </Typography>
          ))}
        </Box>
        <Box position="relative" height={H} sx={{ borderBottom: `1px solid ${tokens.borderStrong}` }}>
          {[50, 100].map(v => (
            <Box key={v} sx={{ position: 'absolute', left: 0, right: 0, bottom: `${v}%`, borderTop: `1px dashed ${tokens.border}` }} />
          ))}
          <Box position="absolute" sx={{ inset: 0 }} display="grid" gridTemplateColumns={`repeat(${ranges.length}, minmax(0,1fr))`} gap={{ xs: 1, sm: 2 }} px={{ xs: 0.5, sm: 2 }}>
            {ranges.map(r => {
              const expected = EXPECTED[r.range] ?? 0.5;
              return (
                <Tooltip key={r.range} arrow title={`${r.range}: won ${r.correct} of ${r.total} (${Math.round(r.accuracy)}%), expected ~${Math.round(expected * 100)}%`}>
                  <Box position="relative" height="100%" sx={{ cursor: 'default' }}>
                    <Box sx={{
                      position: 'absolute', bottom: 0, left: '18%', right: '18%', height: `${r.accuracy}%`,
                      bgcolor: tokens.accent, borderRadius: '4px 4px 0 0', minHeight: r.accuracy > 0 ? 2 : 0,
                    }} />
                    <Box sx={{ position: 'absolute', left: '6%', right: '6%', bottom: `${expected * 100}%`, height: 2, bgcolor: tokens.text, boxShadow: `0 0 0 1px ${tokens.surface}` }} />
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
        <Box />
        <Box display="grid" gridTemplateColumns={`repeat(${ranges.length}, minmax(0,1fr))`} gap={{ xs: 1, sm: 2 }} px={{ xs: 0.5, sm: 2 }}>
          {ranges.map(r => (
            <Box key={r.range} textAlign="center">
              <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700, fontSize: { xs: 13, sm: 15 } }}>{Math.round(r.accuracy)}%</Typography>
              <Typography variant="caption" display="block" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: 10, sm: 12 } }}>{r.range}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>n={r.total}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function Overview() {
  const [threshold, setThreshold] = useState(60);
  const [debounced, setDebounced] = useState(60);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(threshold), 300);
    return () => clearTimeout(t);
  }, [threshold]);

  const { data: s, loading, error, reload } = useApi(`/api/stats?confidence=${debounced / 100}`, { deps: [debounced], refreshMs: 60000 });

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!s) return <Skeleton variant="rounded" height={320} />;

  const ca = s.confidence_analysis;
  const settled = s.completed_predictions;

  return (
    <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity .2s' }}>
      <Box display="grid" gap={{ xs: 1.5, sm: 2 }} gridTemplateColumns={{ xs: 'repeat(2, minmax(0,1fr))', md: 'repeat(4, minmax(0,1fr))' }}>
        <StatTile label="Accuracy" value={settled ? `${Math.round(s.accuracy)}%` : '–'} hint={`${s.correct_predictions} of ${settled} correct`} />
        <StatTile label="Settled" value={settled} hint="matches with a result" />
        <StatTile label="Pending" value={s.pending_predictions} hint="today and upcoming" />
        <StatTile label="Avg probability" value={pct(s.avg_confidence || 0)} hint="what accuracy should approach" />
      </Box>

      <Box display="grid" gap={2} mt={2} gridTemplateColumns={{ xs: '1fr', md: 'minmax(0,3fr) minmax(0,2fr)' }}>
        <Card sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6">Calibration</Typography>
          <Typography variant="body2" color="text.secondary" mb={2.5}>
            How often picks in each probability band actually won. Bars near the line mean the probabilities can be trusted.
          </Typography>
          {ca?.by_range?.length ? <CalibrationChart ranges={ca.by_range} /> : (
            <Box textAlign="center" py={5} color="text.secondary">
              <NoDataIcon sx={{ fontSize: 36, mb: 1 }} />
              <Typography variant="body2">No settled predictions yet.</Typography>
            </Box>
          )}
        </Card>

        <Card sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6">Confidence filter</Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Accuracy if you only followed picks at or above this probability.
          </Typography>
          <Box px={1}>
            <Slider
              value={threshold}
              onChange={(_, v) => setThreshold(v)}
              min={30} max={85} step={5}
              valueLabelDisplay="on"
              valueLabelFormat={(v) => `${v}%`}
              marks={[{ value: 30, label: '30%' }, { value: 85, label: '85%' }]}
              sx={{ mt: 3 }}
            />
          </Box>
          {ca && (
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.5} mt={2}>
              {[['At or above', ca.above_threshold, tokens.accent], ['Below', ca.below_threshold, tokens.textMuted]].map(([label, g, color]) => (
                <Box key={label} sx={{ p: 1.75, borderRadius: 2.5, bgcolor: tokens.surfaceRaised, border: `1px solid ${tokens.border}` }}>
                  <Typography variant="overline" color="text.secondary">{label} {ca.threshold_percent}%</Typography>
                  <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700, fontSize: '1.6rem', color }}>
                    {g.total ? `${Math.round(g.accuracy)}%` : '–'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{g.correct} of {g.total} picks</Typography>
                </Box>
              ))}
            </Box>
          )}
          <Typography variant="caption" color="text.secondary" display="block" mt={2}>
            Small samples swing a lot. Treat bands with fewer than ~30 matches as a rough guide.
          </Typography>
        </Card>
      </Box>
    </Box>
  );
}

function History() {
  const { data, loading, error, reload } = useApi('/api/predictions/history', { refreshMs: 60000 });
  const [filter, setFilter] = useState('all');

  const rows = useMemo(() => (data || [])
    .filter(p => p.result !== 'expired')
    .filter(p => filter === 'all' || (filter === 'correct' ? p.result === p.prediction : p.result !== p.prediction)), [data, filter]);

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (loading) return <Skeleton variant="rounded" height={320} />;

  const settled = (data || []).filter(p => p.result !== 'expired');
  const correct = settled.filter(p => p.result === p.prediction).length;

  if (settled.length === 0) {
    return <EmptyState icon={<EmptyIcon />} title="No settled predictions yet" body="Results are checked every 2 hours once matches finish." />;
  }

  return (
    <Box>
      <ToggleButtonGroup
        size="small" exclusive value={filter} onChange={(_, v) => v && setFilter(v)}
        sx={{ ...segmentedSx, mb: 2 }}
      >
        <ToggleButton value="all">All ({settled.length})</ToggleButton>
        <ToggleButton value="correct">Correct ({correct})</ToggleButton>
        <ToggleButton value="missed">Missed ({settled.length - correct})</ToggleButton>
      </ToggleButtonGroup>

      <Card>
        <Box sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: '110px minmax(0,1fr) 140px 90px 140px 110px', gap: 2, px: 2.5, py: 1.25, borderBottom: `1px solid ${tokens.border}`, bgcolor: alpha('#fff', 0.02) }}>
          {['Date', 'Match', 'Pick', 'Prob.', 'Result', ''].map(h => (
            <Typography key={h} variant="overline" color="text.secondary">{h}</Typography>
          ))}
        </Box>
        {rows.map(p => {
          const won = p.result === p.prediction;
          return (
            <Box key={p.id} sx={{
              display: 'grid', alignItems: 'center', gap: { xs: 0.75, md: 2 }, px: { xs: 2, md: 2.5 }, py: 1.5,
              gridTemplateColumns: { xs: 'minmax(0,1fr) auto', md: '110px minmax(0,1fr) 140px 90px 140px 110px' },
              '&:not(:last-of-type)': { borderBottom: `1px solid ${tokens.border}` },
            }}>
              <Typography variant="caption" color="text.secondary" sx={{ order: { xs: 1, md: 0 } }}>
                {new Date(p.date).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                <Box component="span" sx={{ display: { md: 'none' } }}> · {p.league}</Box>
              </Typography>
              <Box minWidth={0} sx={{ order: { xs: 0, md: 0 }, gridColumn: { xs: '1 / -1', md: 'auto' } }}>
                <Typography variant="body2" fontWeight={600} noWrap>{p.home_team} vs {p.away_team}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: { xs: 'none', md: 'block' } }}>{p.league}</Typography>
              </Box>
              <Box sx={{ order: { xs: 2, md: 0 }, display: { xs: 'none', md: 'block' } }}><OutcomePill outcome={p.prediction} /></Box>
              <Typography variant="body2" fontWeight={600} sx={{ display: { xs: 'none', md: 'block' } }}>{pct(p.confidence)}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>
                {outcomeLabel[p.result] || p.result}
              </Typography>
              <Box display="inline-flex" alignItems="center" gap={0.5} justifySelf={{ xs: 'end', md: 'start' }} sx={{ order: { xs: 2, md: 0 }, color: won ? tokens.status.good : tokens.status.bad, fontWeight: 700, fontSize: 13 }}>
                {won ? <CorrectIcon sx={{ fontSize: 18 }} /> : <MissedIcon sx={{ fontSize: 18 }} />}
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{won ? 'Correct' : 'Missed'}</Box>
                <Box component="span" sx={{ display: { xs: 'inline', md: 'none' }, color: 'text.secondary', fontWeight: 500, ml: 0.5 }}>
                  {outcomeLabel[p.prediction]} {pct(p.confidence)}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Card>
    </Box>
  );
}

const Performance = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const tab = pathname === '/history' ? 'history' : 'overview';

  return (
    <Box>
      <PageHeader
        title="Performance"
        subtitle="How the predictions have done once matches were settled. Only predictions made from real bookmaker odds count (from 15 Oct 2026); older entries used placeholder odds."
      />
      <Tabs
        value={tab}
        onChange={(_, v) => navigate(v === 'history' ? '/history' : '/stats')}
        sx={{ mb: 3, borderBottom: `1px solid ${tokens.border}`, '& .MuiTabs-indicator': { height: 3, borderRadius: 3 } }}
      >
        <Tab value="overview" label="Overview" />
        <Tab value="history" label="History" />
      </Tabs>
      {tab === 'overview' ? <Overview /> : <History />}
    </Box>
  );
};

export default Performance;
